import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import { ApiError } from "../api/client";
import { useAuth } from "../context/AuthContext";

interface Props {
  children: ReactNode;
}

function isValidAdminPinLength(pin: string): boolean {
  return pin.length === 4 || pin.length === 6;
}

export default function AdminPinGate({ children }: Props) {
  const { t } = useTranslation();
  const {
    employee,
    isAdmin,
    isAdminPinVerified,
    verifyAdminPin,
    setInitialAdminPin,
  } = useAuth();
  const navigate = useNavigate();

  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!employee) {
    return <Navigate to="/" replace />;
  }
  if (!isAdmin) {
    return <Navigate to="/home" replace />;
  }
  if (isAdminPinVerified) {
    return <>{children}</>;
  }

  const needsSetup = employee.must_change_admin_pin || !employee.has_admin_pin;

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!isValidAdminPinLength(pin)) {
      setError("Admin PIN must be 4 or 6 digits.");
      return;
    }
    setSubmitting(true);
    try {
      if (needsSetup) {
        if (pin !== confirmPin) {
          setError(t("auth.pinMismatch"));
          return;
        }
        await setInitialAdminPin(pin);
      } else {
        await verifyAdminPin(pin);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || t("auth.incorrectPin"));
      } else {
        setError(t("common.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = () => navigate("/home");

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 md:items-center md:px-4">
      <form
        onSubmit={submit}
        className="w-full rounded-t-xl bg-white p-6 shadow-2xl md:max-w-sm md:rounded-xl"
      >
        <h2 className="text-lg font-semibold text-gray-900">
          {needsSetup
            ? t("auth.adminPin.setupTitle")
            : t("auth.adminPin.title")}
        </h2>
        <p className="mt-2 text-sm text-gray-500">
          {needsSetup
            ? t("auth.adminPin.setupBody")
            : t("auth.adminPin.subtitle")}
        </p>

        <label className="mt-4 block text-sm">
          <span className="block font-medium text-gray-700">
            {needsSetup ? t("auth.newPin") : t("auth.pin")}
          </span>
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            autoFocus
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-base tracking-widest focus:border-pme-blue focus:outline-none focus:ring-2 focus:ring-pme-blue/30"
          />
          <p className="mt-1 text-xs text-gray-400">
            Enter a 4 or 6 digit PIN
          </p>
        </label>

        {needsSetup && (
          <label className="mt-3 block text-sm">
            <span className="block font-medium text-gray-700">
              {t("auth.confirmPin")}
            </span>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-base tracking-widest focus:border-pme-blue focus:outline-none focus:ring-2 focus:ring-pme-blue/30"
            />
          </label>
        )}

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={cancel}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={submitting || !isValidAdminPinLength(pin)}
            className="rounded-lg bg-pme-blue px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {submitting ? t("common.loading") : t("auth.adminPin.verify")}
          </button>
        </div>
      </form>
    </div>
  );
}
